import React, { useState, useRef, useEffect, useContext } from "react";
import Modal from "../adminReproduction/modal";
import StatusChoiceModal from "../adminReproduction/statusChoiceModal";
import DiagnosticModal from "../adminReproduction/DiagnosticModal";
import { updateReproduction, updateReproductionE } from "../../../api/reproductionApi";
import { deleteAnimal } from "../../../api/animalsApi";
import swal from "sweetalert";
import { useTranslation } from "react-i18next";
import '../adminReproduction/ReproWheel.css'
import repro from '../../../cow.jpg';
import { FiCalendar, FiHash, FiTag, FiEdit2, FiInfo, FiRefreshCw, FiSearch, FiZoomIn, FiZoomOut, FiMaximize, FiAlertCircle, FiClock, FiActivity, FiTrendingUp } from "react-icons/fi";
import { GiCow } from "react-icons/gi";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../../../Contexts/AuthContext";

const CYCLE_MONTHS = 24;
const CYCLE_DAYS = CYCLE_MONTHS * 30;

const statusColors = {
    croissance: "#113dffff",
    canMate: "#f1c40f",
    rest: "#27ae60",
};

const statusLegendMap = {
    "croissance": "croissance",
    "canMate": "canMate",
    "rest": "rest",
};

const ringRadius = 500;
const circumference = 2 * Math.PI * ringRadius;

const segments = [
    { label: "Repos", color: "#27ae60", value: 60.5 / 365 },
    { label: "Croissance", color: "#11a0ffff", value: 17.8 / 365 },
    { label: "Croissance", color: "#0f1ef1ff", value: 12.5 / 365 },
    { label: "Croissance", color: "#11a0ffff", value: 91.5 / 365 },
    { label: "Croissance", color: "#0746f3f0", value: 61 / 365 },
    { label: "Saillie", color: "#fcff4bf0", value: 45 / 365 },
    { label: "Repos", color: "#27ae60", value: 80 / 365 },
];

function daysBetween(from, to) {
    return Math.floor((to - from) / (1000 * 60 * 60 * 24));
}

function getMonthAngle(dateString) {
    const d = new Date(dateString);
    const month = d.getMonth();
    const day = d.getDate();
    const dayOfYear = (month * 30) + day;
    return dayOfYear * 0.5;
}

function getDaysInMonth(year, month) {
    return new Date(year, month + 1, 0).getDate();
}

function getGrowthPhase(ageDays) {
    if (ageDays < 365) return 'croissance';
    if (ageDays < 455) return 'canMate';
    return 'rest';
}

function getGrowthAttention(reproduction, today) {
    const birthDate = new Date(reproduction.animal?.date_naissance);
    if (isNaN(birthDate.getTime())) return { level: 'stable', score: 0, labelKey: 'onTrack', label: 'On track', meta: '' };

    const ageDays = daysBetween(birthDate, today);
    const ageMonths = Math.floor(ageDays / 30);
    const genre = reproduction.animal?.genre;

    if (ageDays >= 365 && ageDays < 455 && genre === 'femelle') {
        return { level: 'warning', score: 80, labelKey: 'readyToMate', label: 'Ready to mate', metaKey: 'ageMonths', meta: `${ageMonths}m old`, days: ageMonths };
    }
    if (ageDays >= 660) {
        return { level: 'critical', score: 90, labelKey: 'nearingMaturity', label: 'Nearing maturity limit', metaKey: 'ageMonths', meta: `${ageMonths}m old`, days: ageMonths };
    }
    if (ageDays >= 365 && genre === 'male') {
        return { level: 'watch', score: 60, labelKey: 'maleReadyForSale', label: 'Male ready for sale', metaKey: 'ageMonths', meta: `${ageMonths}m old`, days: ageMonths };
    }
    return { level: 'stable', score: 0, labelKey: 'growing', label: 'Growing', metaKey: 'ageMonths', meta: `${ageMonths}m old`, days: ageMonths };
}

export default function CroissanceWheel({ reproductions, onStatusChange }) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { user } = useContext(AuthContext);
    const isEleveur = user?.role === 'eleveur';
    const radius = 450;
    const center = 510;
    const today = new Date();
    const monthNames = t("croissanceWheel.monthNames", { returnObjects: true });
    const cycleLabels = t("croissanceWheel.cycleLabels", { returnObjects: true });
    const todayDateString = today.toISOString().split('T')[0];
    const todayAngle = getMonthAngle(todayDateString);
    const rotationAngle = 180.7 - todayAngle;

    const getPhaseLabel = (phase) => {
        const key = statusLegendMap[phase];
        return key ? t(`croissanceWheel.legend.${key}`) : phase;
    };

    const getAttentionLabel = (key, fallback) => t(`croissanceWheel.attentionLabels.${key}`, { defaultValue: fallback });

    const [selected, setSelected] = useState(null);
    const [showChoiceModal, setShowChoiceModal] = useState(false);
    const [showDiagnostic, setShowDiagnostic] = useState(false);
    const [zoom, setZoom] = useState(1);
    const [pan, setPan] = useState({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState(false);
    const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
    const [activeFilter, setActiveFilter] = useState('all');
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResult, setSearchResult] = useState(null);

    const containerRef = useRef(null);
    const svgRef = useRef(null);

    const eligibleAnimals = reproductions.filter((r) => {
        if (!r?.animal || !r?.status) return false;
        const dateN = new Date(r.animal.date_naissance);
        if (isNaN(dateN.getTime())) return false;
        const daysOld = (today - dateN) / (1000 * 60 * 60 * 24);
        return daysOld < 720 && !r.date_saille && !r.date_gestation && !r.date_velage && !r.date_avortement;
    });

    const normalizedQuery = searchQuery.trim().toLowerCase();

    const filteredAnimals = eligibleAnimals.filter((r) => {
        const ageDays = daysBetween(new Date(r.animal.date_naissance), today);
        const phase = getGrowthPhase(ageDays);
        const attention = getGrowthAttention(r, today);
        const genre = r.animal?.genre;

        const matchesFilter = activeFilter === 'all'
            ? true
            : activeFilter === 'attention'
                ? attention.level !== 'stable'
                : activeFilter === 'male'
                    ? genre === 'male'
                    : activeFilter === 'female'
                        ? genre === 'femelle'
                        : phase === activeFilter;

        const matchesQuery = !normalizedQuery ||
            r.animal?.numero?.toString().toLowerCase().includes(normalizedQuery) ||
            r.animal?.nom?.toLowerCase().includes(normalizedQuery);

        return matchesFilter && matchesQuery;
    });

    const maleCount = eligibleAnimals.filter((r) => r.animal?.genre === 'male').length;
    const femaleCount = eligibleAnimals.filter((r) => r.animal?.genre === 'femelle').length;
    const readyToMateCount = eligibleAnimals.filter((r) => {
        const ageDays = daysBetween(new Date(r.animal.date_naissance), today);
        return ageDays >= 365 && ageDays < 455 && r.animal?.genre === 'femelle';
    }).length;
    const nearingMaturityCount = eligibleAnimals.filter((r) => {
        const ageDays = daysBetween(new Date(r.animal.date_naissance), today);
        return ageDays >= 600;
    }).length;

    const attentionQueue = eligibleAnimals
        .map((r) => {
            const a = getGrowthAttention(r, today);
            a.translatedLabel = getAttentionLabel(a.labelKey, a.label);
            a.translatedMeta = a.metaKey ? t(`croissanceWheel.attentionLabels.${a.metaKey}`, { days: a.days, defaultValue: a.meta }) : a.meta;
            return { reproduction: r, attention: a };
        })
        .filter(({ attention }) => attention.level !== 'stable')
        .sort((a, b) => b.attention.score - a.attention.score)
        .slice(0, 5);

    const statusBreakdown = ['croissance', 'canMate', 'rest']
        .map((phase) => ({
            phase,
            color: statusColors[phase],
            count: eligibleAnimals.filter((r) => {
                const ageDays = daysBetween(new Date(r.animal.date_naissance), today);
                return getGrowthPhase(ageDays) === phase;
            }).length
        }))
        .filter((item) => item.count > 0);

    const featuredAnimal = selected || searchResult || attentionQueue[0]?.reproduction || filteredAnimals[0] || null;
    const featuredAttention = featuredAnimal ? (() => {
        const a = getGrowthAttention(featuredAnimal, today);
        a.translatedLabel = getAttentionLabel(a.labelKey, a.label);
        a.translatedMeta = a.metaKey ? t(`croissanceWheel.attentionLabels.${a.metaKey}`, { days: a.days, defaultValue: a.meta }) : a.meta;
        return a;
    })() : null;

    const filterOptions = [
        { key: 'all', label: t('croissanceWheel.filters.all') },
        { key: 'attention', label: t('croissanceWheel.filters.attention') },
        { key: 'croissance', label: t('croissanceWheel.legend.croissance') },
        { key: 'canMate', label: t('croissanceWheel.legend.canMate') },
        { key: 'male', label: t('croissanceWheel.filters.male') },
        { key: 'female', label: t('croissanceWheel.filters.female') },
    ];

    useEffect(() => {
        if (!normalizedQuery) { setSearchResult(null); return; }
        const found = eligibleAnimals.find((r) =>
            r.animal?.numero?.toString().toLowerCase().includes(normalizedQuery) ||
            r.animal?.nom?.toLowerCase().includes(normalizedQuery)
        );
        setSearchResult(found || null);
    }, [normalizedQuery, reproductions]);

    const handleMouseDown = (e) => {
        if (e.button === 0) {
            setIsDragging(true);
            setDragStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
        }
    };

    const handleWheel = (e) => {
        if (!e.ctrlKey) return;
        e.preventDefault();
        if (!containerRef.current) return;
        const rect = containerRef.current.getBoundingClientRect();
        const mouseX = e.clientX - rect.left;
        const mouseY = e.clientY - rect.top;
        const delta = e.deltaY * -0.001;
        const newZoom = Math.min(Math.max(0.5, zoom + delta), 2);
        const zoomPoint = { x: (mouseX - pan.x) / zoom, y: (mouseY - pan.y) / zoom };
        setZoom(newZoom);
        setPan({ x: mouseX - zoomPoint.x * newZoom, y: mouseY - zoomPoint.y * newZoom });
    };

    const handleMouseMove = (e) => {
        if (isDragging) setPan({ x: e.clientX - dragStart.x, y: e.clientY - dragStart.y });
    };

    const handleMouseUp = () => setIsDragging(false);

    function openPopup(repro) { setSelected(repro); }
    function closePopup() { setSelected(null); }

    function handleChangeStatus(repro) {
        setSelected(repro);
        setShowChoiceModal(true);
    }

    async function handleDelete(id) {
        swal({
            title: t("croissanceWheel.swal.confirmSellTitle"),
            text: t("croissanceWheel.swal.confirmSellText"),
            icon: "info",
            buttons: true,
        }).then(async (good) => {
            if (good) {
                try {
                    await deleteAnimal(id);
                    swal(t("croissanceWheel.swal.sellSuccessTitle"), t("croissanceWheel.swal.sellSuccessText"), { icon: "success" });
                } catch (e) { console.error(e); swal(t("croissanceWheel.swal.sellError")); }
            } else {
                swal(t("croissanceWheel.swal.sellError"));
            }
        });
    }

    function onSelectNext(next) {
        if (!selected || !selected.id) return;
        setShowChoiceModal(false);
        if (next === "Diagnostic") { setShowDiagnostic(true); return; }
        const statusToSend = next === "Supprimer événement" ? "previous" : next;
        onStatusChange(selected.id, statusToSend)
            .then(() => { setShowChoiceModal(false); setSelected(null); })
            .catch(err => { console.error("change status failed:", err.response?.data || err.message || err); setShowChoiceModal(false); });
    }

    function onDiagnosticResult(result) {
        const id = selected?.id;
        setShowDiagnostic(false);
        setSelected(null);
        const saillieDate = new Date(selected.date_saille);
        const prevuVelgeCalcul = new Date(saillieDate.getTime() + (283 * 24 * 60 * 60 * 1000));
        const formattedDate = prevuVelgeCalcul.toISOString().slice(0, 19).replace('T', ' ');
        if (result === "positif") {
            onStatusChange(id, "Gestation");
            const updateRequest = isEleveur ? updateReproductionE : updateReproduction;
            updateRequest(id, { date_gestation: selected.date_saille, date_prevu_velage: formattedDate });
        } else {
            onStatusChange(id, "Velage");
        }
    }

    return (
        <>
            <Modal show={!!selected && !showChoiceModal && !showDiagnostic} onClose={closePopup}>
                {selected && (() => {
                    const birthDate = new Date(selected.animal.date_naissance);
                    const isValidDate = !isNaN(birthDate.getTime());
                    const ageInDays = isValidDate ? Math.floor((today - birthDate) / (1000 * 60 * 60 * 24)) : null;
                    const ageMonths = ageInDays !== null ? Math.floor(ageInDays / 30) : null;
                    const canChangeStatus = ageInDays >= 365 && selected?.animal?.genre === "femelle";
                    const isMale = selected?.animal?.genre === "male";
                    const phase = ageInDays !== null ? getGrowthPhase(ageInDays) : 'croissance';
                    const displayPhase = isMale
                        ? {
                            label: t('croissanceWheel.legend.croissance'),
                            color: statusColors.croissance,
                        }
                        : {
                            label: getPhaseLabel(phase),
                            color: statusColors[phase] || '#113dffff',
                        };
                    return (
                        <div className="rp-detail-modal">
                            <h2 className="rp-modal-title"><GiCow /> {t("croissanceWheel.details.title")}</h2>
                            <div className="rp-detail-grid">
                                <div className="rp-detail-item">
                                    <span className="rp-detail-label"><FiHash /> {t("croissanceWheel.details.number")}</span>
                                    <span className="rp-detail-value">{selected.animal.numero}</span>
                                </div>
                                <div className="rp-detail-item">
                                    <span className="rp-detail-label"><GiCow /> {t("croissanceWheel.details.name")}</span>
                                    <span className="rp-detail-value">{selected.animal.nom}</span>
                                </div>
                                <div className="rp-detail-item">
                                    <span className="rp-detail-label"><FiTag /> {t("croissanceWheel.details.gender")}</span>
                                    <span className="rp-detail-value">{selected.animal.genre}</span>
                                </div>
                                <div className="rp-detail-item">
                                    <span className="rp-detail-label"><FiCalendar /> {t("croissanceWheel.details.birthDate")}</span>
                                    <span className="rp-detail-value">{selected.animal.date_naissance}</span>
                                </div>
                                {ageMonths !== null && (
                                    <div className="rp-detail-item">
                                        <span className="rp-detail-label"><FiClock /> {t("croissanceWheel.details.age")}</span>
                                        <span className="rp-detail-value">{ageMonths} {t('croissanceWheel.control.months')}</span>
                                    </div>
                                )}
                                <div className="rp-detail-item">
                                    <span className="rp-detail-label"><FiActivity /> {t("croissanceWheel.details.phase")}</span>
                                    <span className="rp-detail-value rp-status-pill" style={{ backgroundColor: displayPhase.color }}>
                                        {displayPhase.label}
                                    </span>
                                </div>
                            </div>
                            <div className="rp-detail-actions">
                                <a href={`${isEleveur ? '/eleveur' : '/admin'}/animal/details/${selected.animal.id}`} className="rp-action-btn rp-btn-info">
                                    <FiInfo /> {t("croissanceWheel.details.btnDetails")}
                                </a>
                                <a href={`${isEleveur ? '/eleveur' : '/admin'}/edit/${selected.animal.id}`} className="rp-action-btn rp-btn-edit">
                                    <FiEdit2 /> {t("croissanceWheel.details.btnEdit")}
                                </a>
                                {canChangeStatus && (
                                    <button type="button" onClick={() => handleChangeStatus(selected)} className="rp-action-btn rp-btn-status">
                                        <FiRefreshCw /> {t("croissanceWheel.details.btnChangeStatus")}
                                    </button>
                                )}
                                {!isEleveur && isMale && (
                                    <button type="button" onClick={() => handleDelete(selected.animal.id)} className="rp-action-btn rp-btn-status">
                                        <FiTag /> {t("croissanceWheel.details.btnSell")}
                                    </button>
                                )}
                            </div>
                        </div>
                    );
                })()}
            </Modal>

            <StatusChoiceModal
                show={showChoiceModal}
                currentStatus={selected?.status?.titre}
                animalGender={selected?.animal?.genre}
                animalBirthDate={selected?.animal?.date_naissance}
                onSelect={onSelectNext}
                onClose={() => setShowChoiceModal(false)}
            />

            <DiagnosticModal
                show={showDiagnostic}
                onResult={onDiagnosticResult}
                onClose={() => setShowDiagnostic(false)}
            />

            <div className="rp-command-center">
                <div className="rp-command-glow rp-command-glow--one"></div>
                <div className="rp-command-glow rp-command-glow--two"></div>

                <section className="rp-hero-band">
                    <div className="rp-hero-copy">
                        <span className="rp-hero-eyebrow">{t('croissanceWheel.control.badge')}</span>
                        <h1 className="rp-hero-title">{t('croissanceWheel.control.title')}</h1>
                        <p className="rp-hero-text">{t('croissanceWheel.control.subtitle')}</p>
                    </div>
                    <div className="rp-hero-signal">
                        <span className="rp-hero-signal-label">{t('croissanceWheel.control.liveDate')}</span>
                        <strong>{todayDateString}</strong>
                        <span>{filteredAnimals.length} / {eligibleAnimals.length} {t('croissanceWheel.control.visibleAnimals')}</span>
                    </div>
                </section>

                <section className="rp-kpi-grid">
                    <article className="rp-kpi-card">
                        <span className="rp-kpi-icon"><GiCow /></span>
                        <span className="rp-kpi-label">{t('croissanceWheel.control.totalYoung')}</span>
                        <strong className="rp-kpi-value">{eligibleAnimals.length}</strong>
                        <span className="rp-kpi-meta">{t('croissanceWheel.control.totalYoungMeta')}</span>
                    </article>
                    <article className="rp-kpi-card">
                        <span className="rp-kpi-icon"><FiTrendingUp /></span>
                        <span className="rp-kpi-label">{t('croissanceWheel.control.readyToMate')}</span>
                        <strong className="rp-kpi-value">{readyToMateCount}</strong>
                        <span className="rp-kpi-meta">{t('croissanceWheel.control.readyToMateMeta')}</span>
                    </article>
                    <article className="rp-kpi-card">
                        <span className="rp-kpi-icon"><FiClock /></span>
                        <span className="rp-kpi-label">{t('croissanceWheel.control.nearingLimit')}</span>
                        <strong className="rp-kpi-value">{nearingMaturityCount}</strong>
                        <span className="rp-kpi-meta">{t('croissanceWheel.control.nearingLimitMeta')}</span>
                    </article>
                    <article className="rp-kpi-card rp-kpi-card--alert">
                        <span className="rp-kpi-icon"><FiAlertCircle /></span>
                        <span className="rp-kpi-label">{t('croissanceWheel.control.attention')}</span>
                        <strong className="rp-kpi-value">{attentionQueue.length}</strong>
                        <span className="rp-kpi-meta">{t('croissanceWheel.control.attentionMeta')}</span>
                    </article>
                </section>

                <section className="rp-toolbar-band">
                    <div className="rp-search-shell">
                        <FiSearch className="rp-search-shell-icon" />
                        <input
                            type="text"
                            className="rp-search-shell-input"
                            placeholder={t('croissanceWheel.searchPlaceholder')}
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                    </div>
                    <div className="rp-chip-row">
                        {filterOptions.map((filter) => (
                            <button
                                key={filter.key}
                                type="button"
                                className={`rp-chip ${activeFilter === filter.key ? 'is-active' : ''}`}
                                onClick={() => setActiveFilter(filter.key)}
                            >
                                {filter.label}
                            </button>
                        ))}
                    </div>
                </section>

                <div className="rp-workspace-grid">
                    <section className="rp-visual-panel">
                        <div className="rp-panel-head">
                            <div>
                                <span className="rp-panel-kicker">{t('croissanceWheel.control.canvasKicker')}</span>
                                <h2 className="rp-panel-title">{t('croissanceWheel.control.canvasTitle')}</h2>
                                <p className="rp-panel-text">{t('croissanceWheel.control.canvasText')}</p>
                            </div>
                            <div className="rp-panel-actions">
                                <div className="rp-zoom-controls">
                                    <button className="rp-zoom-btn" onClick={() => setZoom(z => Math.min(z + 0.15, 2))} title="Zoom in"><FiZoomIn /></button>
                                    <button className="rp-zoom-btn" onClick={() => { setZoom(1); setPan({ x: 0, y: 0 }); }} title="Reset"><FiMaximize /></button>
                                    <button className="rp-zoom-btn" onClick={() => setZoom(z => Math.max(z - 0.15, 0.5))} title="Zoom out"><FiZoomOut /></button>
                                </div>
                            </div>
                        </div>

                        {filteredAnimals.length === 0 && (
                            <div className="rp-empty-state">
                                <strong>{t('croissanceWheel.control.emptyTitle')}</strong>
                                <span>{t('croissanceWheel.control.emptyText')}</span>
                            </div>
                        )}

                        <div
                            className="rp-wheel-container"
                            ref={containerRef}
                            style={{ cursor: isDragging ? "grabbing" : "grab" }}
                            onWheel={handleWheel}
                            onMouseMove={handleMouseMove}
                            onMouseDown={handleMouseDown}
                            onMouseUp={handleMouseUp}
                            onMouseLeave={handleMouseUp}
                        >
                            <div className="rp-wheel-statusbar">
                                <span className="rp-wheel-status-pill">{t('croissanceWheel.control.focused')}: {activeFilter === 'all' ? t('croissanceWheel.filters.all') : filterOptions.find((f) => f.key === activeFilter)?.label}</span>
                                <span className="rp-wheel-status-pill">{t('croissanceWheel.control.matching')}: {filteredAnimals.length}</span>
                            </div>

                            <div className="rp-wheel-inner">
                                {featuredAnimal && (
                                    <div className={`rp-center-card rp-center-card--${featuredAttention?.level || 'stable'}`}>
                                        <span className="rp-center-card-label">{t('croissanceWheel.control.centerTitle')}</span>
                                        <strong>{featuredAnimal.animal?.numero}</strong>
                                        <span>{getPhaseLabel(getGrowthPhase(daysBetween(new Date(featuredAnimal.animal?.date_naissance), today)))}</span>
                                        <small>{featuredAttention?.translatedLabel}</small>
                                    </div>
                                )}

                                <svg
                                    ref={svgRef}
                                    viewBox="-200 -100 1513 1300"
                                    preserveAspectRatio="xMidYMid meet"
                                    className="rp-wheel-svg"
                                    style={{
                                        transform: `translate(${pan.x}px, 0px) scale(${zoom})`,
                                        transformOrigin: "center center",
                                        transition: isDragging ? "none" : "transform 0.1s ease-out"
                                    }}
                                >
                                    {(() => {
                                        let dashOffset = 0;
                                        const todayStartAngle = 30.3;
                                        const todayStartAngleRec = 30;

                                        return (
                                            <g>
                                                <g transform={`rotate(${todayStartAngle}, ${center}, ${center})`}>{segments.map((seg, i) => {
                                                    const dash = seg.value * circumference;
                                                    const element = (
                                                        <circle
                                                            key={i}
                                                            cx={center}
                                                            cy={center}
                                                            r={ringRadius}
                                                            fill="none"
                                                            stroke={seg.color}
                                                            strokeWidth="22"
                                                            strokeLinecap="butt"
                                                            strokeDasharray={`${dash} ${circumference}`}
                                                            strokeDashoffset={-dashOffset}
                                                        />
                                                    );
                                                    dashOffset += dash;
                                                    return element;
                                                })}</g>
                                                <g transform={`rotate(${todayStartAngleRec}, ${center}, ${center})`}>
                                                    {(() => {
                                                        const labeledIndices = [0, 3, 6, 9, 12, 15, 16, 18, 21, 24, 27, 30, 33, 36, 39, 42, 45, 48, 51, 54, 57, 60, 63, 66, 69];
                                                        const labeled = cycleLabels;
                                                        return Array.from({ length: 72 }, (_, i) => {
                                                            const angle = i * 5;
                                                            let x = center + ringRadius * Math.cos(angle * Math.PI / 180);
                                                            let y = center + ringRadius * Math.sin(angle * Math.PI / 180);
                                                            const elements = [
                                                                <rect key={i} x={x - 10} y={y - 2.5} width="20" height="3" fill="black" transform={`rotate(${angle}, ${x}, ${y})`} />
                                                            ];
                                                            if (labeledIndices.includes(i)) {
                                                                const textRadius = ringRadius + 90;
                                                                let LineRadius = ringRadius + 60;
                                                                let textX = center + textRadius * Math.cos(angle * Math.PI / 180);
                                                                let textY = center + textRadius * Math.sin(angle * Math.PI / 180);
                                                                let lineX = center + LineRadius * Math.cos(angle * Math.PI / 180);
                                                                let lineY = center + LineRadius * Math.sin(angle * Math.PI / 180);

                                                                const labelIndex = labeledIndices.indexOf(i);
                                                                if (labelIndex === 6) {
                                                                    LineRadius = ringRadius + 40;
                                                                    lineX = center + LineRadius * Math.cos(angle * Math.PI / 180);
                                                                    lineY = center + LineRadius * Math.sin(angle * Math.PI / 180);
                                                                    textX = center - 15 + textRadius * Math.cos(angle * Math.PI / 180);
                                                                    textY = center - 25 + textRadius * Math.sin(angle * Math.PI / 180);
                                                                    y = center + 5 + ringRadius * Math.sin(angle * Math.PI / 180);
                                                                    x = center + 25 + ringRadius * Math.cos(angle * Math.PI / 180);
                                                                }
                                                                if (labelIndex === 4) {
                                                                    LineRadius = ringRadius + 30;
                                                                    lineX = center + LineRadius * Math.cos(angle * Math.PI / 180);
                                                                    lineY = center + LineRadius * Math.sin(angle * Math.PI / 180);
                                                                    y = center + ringRadius * Math.sin(angle * Math.PI / 180);
                                                                    x = center + ringRadius * Math.cos(angle * Math.PI / 180);
                                                                }
                                                                elements.push(<React.Fragment key={`label-${i}`}>
                                                                    {labelIndex === 4 && <line x1={lineX + 12} y1={lineY - 6.5} x2={x + 1} y2={lineY + 9} stroke="#640303ff" strokeWidth="2" />}
                                                                    <line x1={lineX} y1={lineY} x2={x} y2={y} stroke={labelIndex === 2 ? "#640303ff" : '#000000ff'} strokeWidth={labelIndex === 2 ? "#fc0404ff" : '#000000ff'} />
                                                                    <text x={textX} y={textY} fontSize="15" textAnchor="middle" alignmentBaseline="middle" fill="black" transform={`rotate(${-todayStartAngleRec}, ${textX}, ${textY})`}>
                                                                        {labeled[labelIndex].split('\n').map((line, li) => (
                                                                            <tspan key={li} x={textX} dy={li === 0 ? 0 : 12}>{line}</tspan>
                                                                        ))}
                                                                    </text>
                                                                </React.Fragment>);
                                                            }
                                                            return elements;
                                                        });
                                                    })()}
                                                </g>
                                            </g>
                                        );
                                    })()}

                                    <circle cx={center} cy={center} r={radius} fill="white" stroke="#333" strokeWidth="3" />

                                    {(() => {
                                        const angle = 90;
                                        const x = center + (radius - 40) * Math.cos(angle * Math.PI / 180);
                                        const y = center + (radius - 40) * Math.sin(angle * Math.PI / 180);
                                        return (
                                            <g>
                                                <line x1={x} y1={y + 40} x2={center} y2={center} stroke="#ff0000ff" />
                                                <text x={x} y={y + 140} fontSize="20" textAnchor="middle" fill="red">
                                                    {t("croissanceWheel.todayLabel")}
                                                </text>
                                            </g>
                                        );
                                    })()}

                                    <g transform={`rotate(${rotationAngle}, ${center}, ${center})`}>
                                        {Array.from({ length: 24 }, (_, i) => {
                                            const m = monthNames[i % 12];
                                            const angle = i * (360 / 24) - 90;
                                            const x = center + radius * Math.cos(angle * Math.PI / 180);
                                            const y = center + radius * Math.sin(angle * Math.PI / 180);
                                            const textX = center + (radius + 20) * Math.cos(angle * Math.PI / 180);
                                            const textY = center + (radius + 20) * Math.sin(angle * Math.PI / 180);
                                            return (
                                                <g key={i}>
                                                    <line x1={center} y1={center} x2={x} y2={y} stroke="#bbb" strokeWidth="1" />
                                                    <text x={textX} y={textY} fontSize="12" textAnchor="middle" alignmentBaseline="middle" transform={`rotate(${-rotationAngle}, ${textX}, ${textY})`}>{m}</text>
                                                </g>
                                            );
                                        })}

                                        {monthNames.map((m, i) => {
                                            const year = today.getFullYear();
                                            const daysInMonth = getDaysInMonth(year, i);
                                            const monthStartAngle = i * 15 - 90;
                                            const dayAngleStep = 15 / daysInMonth;
                                            return Array.from({ length: Math.floor(daysInMonth / 5) }, (_, j) => {
                                                const day = (j + 1) * 5;
                                                const angle = monthStartAngle + ((day - 1) * dayAngleStep);
                                                const x1 = center + (radius - 60) * Math.cos(angle * Math.PI / 180);
                                                const y1 = center + (radius - 60) * Math.sin(angle * Math.PI / 180);
                                                const x2 = center + (radius - 80) * Math.cos(angle * Math.PI / 180);
                                                const y2 = center + (radius - 80) * Math.sin(angle * Math.PI / 180);
                                                const textX = center + (radius - 90) * Math.cos(angle * Math.PI / 180);
                                                const textY = center + (radius - 90) * Math.sin(angle * Math.PI / 180);
                                                return (
                                                    <g key={`${i}-${day}`}>
                                                        <line x1={x1} y1={y1} x2={x2} y2={y2} stroke="#ccc" strokeWidth="1" />
                                                        <text x={textX} y={textY} fontSize="8" textAnchor="middle" alignmentBaseline="middle" fill="#666" transform={`rotate(${-rotationAngle}, ${textX}, ${textY})`}>{day}</text>
                                                    </g>
                                                );
                                            });
                                        })}

                                        {monthNames.map((m, i) => {
                                            const year = today.getFullYear();
                                            const daysInMonth = getDaysInMonth(year, i);
                                            const monthStartAngle = i * 15 + 90;
                                            const dayAngleStep = 15 / daysInMonth;
                                            return Array.from({ length: Math.floor(daysInMonth / 5) }, (_, j) => {
                                                const day = (j + 1) * 5;
                                                const angle = monthStartAngle + ((day - 1) * dayAngleStep);
                                                const x1 = center + (radius - 60) * Math.cos(angle * Math.PI / 180);
                                                const y1 = center + (radius - 60) * Math.sin(angle * Math.PI / 180);
                                                const x2 = center + (radius - 80) * Math.cos(angle * Math.PI / 180);
                                                const y2 = center + (radius - 80) * Math.sin(angle * Math.PI / 180);
                                                const textX = center + (radius - 90) * Math.cos(angle * Math.PI / 180);
                                                const textY = center + (radius - 90) * Math.sin(angle * Math.PI / 180);
                                                return (
                                                    <g key={`${i}-${day}-b`}>
                                                        <line x1={x1} y1={y1} x2={x2} y2={y2} stroke="#ccc" strokeWidth="1" />
                                                        <text x={textX} y={textY} fontSize="8" textAnchor="middle" alignmentBaseline="middle" fill="#666" transform={`rotate(${-rotationAngle}, ${textX}, ${textY})`}>{day}</text>
                                                    </g>
                                                );
                                            });
                                        })}

                                        {(() => {
                                            const animalsByAngle = new Map();
                                            filteredAnimals.forEach((r, index) => {
                                                if (!r?.animal || !r?.status) return;
                                                const dateN = new Date(r.animal.date_naissance);
                                                const dateNDiff = (today - dateN) / (1000 * 60 * 60 * 24);
                                                if (dateNDiff > 720 || (r.date_saille || r.date_gestation || r.date_velage || r.date_avortement)) return;
                                                const birthDate = new Date(r.animal.date_naissance);
                                                if (isNaN(birthDate.getTime())) return;
                                                const ageDays = daysBetween(birthDate, today);
                                                if (ageDays > CYCLE_DAYS) return;
                                                if (r.date_saille || r.date_gestation || r.date_velage) return;
                                                const diffDays = Math.max(0, daysBetween(birthDate, today));
                                                const cycleDay = diffDays % CYCLE_DAYS;
                                                const angle = (cycleDay / CYCLE_DAYS) * 360 + todayAngle - 91;
                                                const GROUPING_THRESHOLD = 4;
                                                const angleKey = Math.round(10 / GROUPING_THRESHOLD) * GROUPING_THRESHOLD;
                                                if (!animalsByAngle.has(angleKey)) animalsByAngle.set(angleKey, []);
                                                animalsByAngle.get(angleKey).push({ reproduction: r, angle, status: "Croissance", index });
                                            });

                                            const elements = [];
                                            animalsByAngle.forEach((animals) => {
                                                const count = animals.length;
                                                animals.forEach((animal, posIndex) => {
                                                    const { reproduction: r, angle, index } = animal;
                                                    const isSearchMatch = searchResult && searchResult.id === r.id;
                                                    let radiusOffset = 0;
                                                    if (count > 0) radiusOffset = ((posIndex - count / 2) * 45);
                                                    const adjustedRadius = radius - 40 + radiusOffset;
                                                    const x = center + adjustedRadius * Math.cos(angle * Math.PI / 180);
                                                    const y = center + adjustedRadius * Math.sin(angle * Math.PI / 180);
                                                    const dateNa = new Date(r.animal.date_naissance);
                                                    const dateNDiffa = (today - dateNa) / (1000 * 60 * 60 * 24);

                                                    let colorTr = statusColors.croissance;
                                                    if (dateNDiffa >= 365 && dateNDiffa < 455) colorTr = statusColors.canMate;
                                                    else if (dateNDiffa >= 455) colorTr = statusColors.rest;

                                                    const triangleSize = 30;
                                                    const p1x = x + triangleSize * Math.cos(-120 * Math.PI / 180);
                                                    const p1y = y + triangleSize * Math.sin(-40 * Math.PI / 180);
                                                    const p2x = x + triangleSize * Math.cos(30 * Math.PI / 180);
                                                    const p2y = y + triangleSize * Math.sin(-10 * Math.PI / 180);
                                                    const p3x = x + triangleSize * Math.cos(100 * Math.PI / 180);
                                                    const p3y = y + triangleSize * Math.sin(120 * Math.PI / 180);
                                                    const trianglePoints = `${p1x},${p1y} ${p2x},${p2y} ${p3x},${p3y}`;

                                                    elements.push(
                                                        <g key={index} onClick={() => openPopup(r)} style={{ cursor: "pointer" }}>
                                                            {isSearchMatch && (
                                                                <>
                                                                    <circle cx={x} cy={y} r="25" fill="none" stroke="#FFD700" strokeWidth="3" opacity="0.8">
                                                                        <animate attributeName="r" from="25" to="35" dur="1s" repeatCount="indefinite" />
                                                                        <animate attributeName="opacity" from="0.8" to="0" dur="1s" repeatCount="indefinite" />
                                                                    </circle>
                                                                    <circle cx={x} cy={y} r="25" fill="none" stroke="#FFD700" strokeWidth="4" />
                                                                </>
                                                            )}
                                                            <polygon
                                                                points={trianglePoints}
                                                                fill={colorTr}
                                                                stroke={isSearchMatch ? "#FFD700" : "#000"}
                                                                strokeWidth={isSearchMatch ? "4" : "2"}
                                                            />
                                                            <text x={x} y={y + 5} fontSize="12" fontWeight="bold" textAnchor="middle" fill="white" transform={`rotate(${-rotationAngle}, ${x}, ${y})`}>
                                                                {r.animal?.numero}
                                                            </text>
                                                        </g>
                                                    );
                                                });
                                            });
                                            return elements;
                                        })()}
                                    </g>

                                    <defs>
                                        <clipPath id="circleClipCroissance">
                                            <circle cx={center} cy={center} r="50" />
                                        </clipPath>
                                    </defs>

                                    <image href={repro} x={center - 80} y={center - 50} width="160" clipPath="url(#circleClipCroissance)" preserveAspectRatio="xMidYMid slice" />
                                </svg>
                            </div>
                        </div>
                    </section>

                    <aside className="rp-insight-rail">
                        <section className={`rp-rail-card rp-rail-card--feature rp-rail-card--${featuredAttention?.level || 'stable'}`}>
                            <span className="rp-rail-kicker">{t('croissanceWheel.control.featured')}</span>
                            {featuredAnimal ? (
                                <>
                                    <div className="rp-featured-head">
                                        <strong>{featuredAnimal.animal?.numero}</strong>
                                        <span>{featuredAnimal.animal?.nom || t('croissanceWheel.control.unnamed')}</span>
                                    </div>
                                    <div className="rp-featured-meta">
                                        <span>{getPhaseLabel(getGrowthPhase(daysBetween(new Date(featuredAnimal.animal?.date_naissance), today)))}</span>
                                        <span>{featuredAttention?.translatedLabel}</span>
                                    </div>
                                    <div className="rp-featured-grid">
                                        <div>
                                            <label>{t('croissanceWheel.details.birthDate')}</label>
                                            <strong>{featuredAnimal.animal?.date_naissance}</strong>
                                        </div>
                                        <div>
                                            <label>{t('croissanceWheel.details.gender')}</label>
                                            <strong>{featuredAnimal.animal?.genre}</strong>
                                        </div>
                                    </div>
                                </>
                            ) : (
                                <span>{t('croissanceWheel.control.noFeature')}</span>
                            )}
                        </section>

                        <section className="rp-rail-card">
                            <div className="rp-rail-header">
                                <h3>{t('croissanceWheel.control.statusMap')}</h3>
                                <span>{eligibleAnimals.length}</span>
                            </div>
                            <div className="rp-status-list">
                                {statusBreakdown.map((item) => (
                                    <div key={item.phase} className="rp-status-row">
                                        <div className="rp-status-main">
                                            <span className="rp-status-dot" style={{ backgroundColor: item.color }}></span>
                                            <span>{getPhaseLabel(item.phase)}</span>
                                        </div>
                                        <div className="rp-status-bar-track">
                                            <div className="rp-status-bar-fill" style={{ width: `${(item.count / Math.max(eligibleAnimals.length, 1)) * 100}%`, backgroundColor: item.color }}></div>
                                        </div>
                                        <strong>{item.count}</strong>
                                    </div>
                                ))}
                            </div>
                        </section>

                        <section className="rp-rail-card">
                            <div className="rp-rail-header">
                                <h3>{t('croissanceWheel.control.queueTitle')}</h3>
                                <span>{attentionQueue.length}</span>
                            </div>
                            <div className="rp-queue-list">
                                {attentionQueue.length === 0 && (
                                    <div className="rp-queue-empty">{t('croissanceWheel.control.queueEmpty')}</div>
                                )}
                                {attentionQueue.map(({ reproduction, attention }) => (
                                    <button
                                        key={reproduction.id}
                                        type="button"
                                        className={`rp-queue-item rp-queue-item--${attention.level}`}
                                        onClick={() => openPopup(reproduction)}
                                    >
                                        <div>
                                            <strong>{reproduction.animal?.numero}</strong>
                                            <span>{getPhaseLabel(getGrowthPhase(daysBetween(new Date(reproduction.animal?.date_naissance), today)))}</span>
                                        </div>
                                        <div>
                                            <strong>{attention.translatedLabel}</strong>
                                            <span>{attention.translatedMeta}</span>
                                        </div>
                                    </button>
                                ))}
                            </div>
                        </section>
                    </aside>
                </div>
            </div>
        </>
    );
}
